/**
 * @file react-hook-form <-> kore Form shim.
 *
 * Pre-wires react-hook-form + zod onto kore's Base UI Form parts so call
 * locations import a single { Form, Field } from "@/lib/forms". kore core stays
 * state-agnostic; this shim owns the RHF wiring for this app. Ported
 * compactly from vouchers' `src/lib/forms.tsx` — same surface, trimmed
 * comments.
 */

import { zodResolver } from "@hookform/resolvers/zod";
import { Form as CoreForm, type FormProps as CoreFormProps, type FormFieldProps } from "@pej/kore";
import type { ReactNode } from "react";
import {
    Controller,
    type ControllerProps,
    type FieldErrors,
    type FieldPath,
    type FieldValues,
    FormProvider,
    get,
    type Path,
    type Resolver,
    type SubmitErrorHandler,
    type UseFormProps,
    type UseFormReturn,
    useForm,
    useFormState,
} from "react-hook-form";
import type { z } from "zod";

export type {
    Control,
    DefaultValues,
    FieldErrors,
    FieldPath,
    FieldValues,
    SubmitHandler,
    UseFormProps,
    UseFormReturn,
} from "react-hook-form";
export {
    Controller,
    FormProvider,
    useFieldArray,
    useForm,
    useFormContext,
    useFormState,
    useWatch,
} from "react-hook-form";
export { zodResolver };

// ── ProblemDetails (RFC 9457, structural) ──────────────────────────────────

/** Per-field violation in an RFC 9457 problem+json body. */
export interface ProblemDetailViolation {
    /** Dot-path to the field, matching the RHF field name (e.g. `items.0.sku`). */
    path: string;
    /** Human-readable message rendered on the field. */
    message: string;
    /** Machine code. Unused here; kept for parity with the server shape. */
    code?: string | null;
}

/**
 * Subset of RFC 9457 the shim maps onto RHF. Declared structurally so this
 * app's `ApiError` (`src/api/error.ts`) is assignable without importing
 * anything from here.
 */
export interface ProblemDetails {
    /** The server's message; the caller's to show. `title` is a reason
     *  phrase ("Bad Request") and never used. */
    detail?: string | null;
    /** Machine code for the top-level problem. */
    code?: string | null;
    /** Per-field violations. */
    errors?: ReadonlyArray<ProblemDetailViolation> | null;
}

// ── firstFieldMessage ──────────────────────────────────────────────────────

/**
 * The message to show where the reader clicked, when a submit failed
 * validation instead of reaching the server.
 *
 * A save bar sits at the foot of a long form, so the field it stopped on can
 * be a screen away — RHF focuses that field (`shouldFocusError`), and this
 * gives the bar something to say besides nothing. Order follows the schema's
 * keys, which is close enough to reading order; the field itself is the
 * authority, and it is highlighted too.
 */
export function firstFieldMessage(errors: FieldErrors): string {
    const [first] = Object.values(flattenFieldErrors(errors));
    return first && first !== "" ? first : "Something on this page needs fixing.";
}

// ── Internal: flatten RHF's nested error tree ──────────────────────────────

/**
 * @internal — exported for tests; not part of the public surface.
 *
 * Walk RHF's nested errors into the dotted-path record Base UI Form's
 * `errors` prop expects, keyed by each `Field.Root`'s `name`. RHF ships no
 * public flattener. A node is a leaf `FieldError` when it has a string
 * `type` property; everything else is recursed into. Numeric array indices
 * become path segments, matching RHF's own `items.0.sku` field naming.
 */
export function flattenFieldErrors(
    errors: FieldErrors,
    basePath = "",
    acc: Record<string, string> = {},
): Record<string, string> {
    for (const [key, value] of Object.entries(errors)) {
        if (value == null) continue;
        const path = basePath ? `${basePath}.${key}` : key;
        if (isFieldErrorLeaf(value)) {
            acc[path] = typeof value.message === "string" ? value.message : "";
        } else if (typeof value === "object") {
            flattenFieldErrors(value as FieldErrors, path, acc);
        }
    }
    return acc;
}

function isFieldErrorLeaf(value: unknown): value is { type: string; message?: unknown } {
    return (
        typeof value === "object" &&
        value !== null &&
        "type" in value &&
        typeof (value as { type?: unknown }).type === "string"
    );
}

// ── applyProblemDetails ────────────────────────────────────────────────────

/**
 * Map a backend RFC 9457 body onto react-hook-form. Each per-field violation
 * whose path names a mounted field becomes a `setError(path, { type: "server",
 * message })`; once bridged through Base UI Form's `errors` prop it lights
 * `aria-invalid` and renders the field's `Form.Message`. Paths are the
 * server's (`items[0].sku`), which RHF reads as `items.0.sku`. Returns the
 * violations no field shows, for the caller to say elsewhere.
 */
export function applyProblemDetails<
    TFieldValues extends FieldValues,
    TViolation extends ProblemDetailViolation = ProblemDetailViolation,
>(
    form: UseFormReturn<TFieldValues, unknown, unknown>,
    problem: ProblemDetails & { errors?: ReadonlyArray<TViolation> | null },
): TViolation[] {
    const unmapped: TViolation[] = [];
    for (const violation of problem.errors ?? []) {
        if (!hasMountedField(form, violation.path)) {
            unmapped.push(violation);
            continue;
        }
        form.setError(violation.path as Path<TFieldValues>, {
            type: "server",
            message: violation.message,
        });
    }
    return unmapped;
}

// A registered field still on screen; an error set elsewhere would show nowhere.
function hasMountedField<TFieldValues extends FieldValues>(
    form: UseFormReturn<TFieldValues, unknown, unknown>,
    path: string,
): boolean {
    const field = get(form.control._fields, path) as { _f?: { mount?: boolean } } | undefined;
    return field?._f?.mount === true;
}

// ── useKoreForm ────────────────────────────────────────────────────────────

export interface UseKoreFormProps<TSchema extends z.ZodType>
    extends Omit<UseFormProps<z.input<TSchema> & FieldValues, unknown, z.output<TSchema> & FieldValues>, "resolver"> {
    /** The zod schema; its resolver is bound automatically. */
    schema: TSchema;
}

/**
 * Convenience over `useForm` that binds `zodResolver(schema)` — the one
 * piece of boilerplate every form shares. Everything else passes straight
 * through, and the return is a plain `UseFormReturn`.
 */
export function useKoreForm<TSchema extends z.ZodType>({
    schema,
    ...props
}: UseKoreFormProps<TSchema>): UseFormReturn<z.input<TSchema> & FieldValues, unknown, z.output<TSchema> & FieldValues> {
    type TFieldValues = z.input<TSchema> & FieldValues;
    type TTransformedValues = z.output<TSchema> & FieldValues;

    const typedSchema = schema as unknown as z.ZodType<TTransformedValues, TFieldValues>;
    const resolver = zodResolver(typedSchema) as Resolver<TFieldValues, unknown, TTransformedValues>;

    return useForm<TFieldValues, unknown, TTransformedValues>({
        ...props,
        resolver,
    });
}

// ── Form (pre-wired) ───────────────────────────────────────────────────────

export interface FormProps<TFieldValues extends FieldValues, TTransformed = TFieldValues>
    extends Omit<CoreFormProps, "onSubmit" | "onInvalid" | "errors" | "children" | "validationMode"> {
    /** The react-hook-form instance (from `useKoreForm` or `useForm`). */
    form: UseFormReturn<TFieldValues, unknown, TTransformed>;
    /** Called with the parsed/transformed values after RHF validation passes. */
    onSubmit?: (values: TTransformed) => void | Promise<void>;
    /** Called when a submit attempt fails validation, with RHF's field errors. */
    onInvalid?: SubmitErrorHandler<TFieldValues>;
    children: ReactNode;
}

/**
 * Pre-wired form: provides RHF context, bridges RHF's `formState.errors`
 * into Base UI Form's `errors` prop (validation lives in RHF; native browser
 * validation is off via `noValidate`), and wraps the core kore `Form`.
 */
export function Form<TFieldValues extends FieldValues, TTransformed = TFieldValues>({
    form,
    onSubmit,
    onInvalid,
    children,
    ...coreProps
}: FormProps<TFieldValues, TTransformed>) {
    return (
        <FormProvider {...form}>
            <FormBridge onSubmit={form.handleSubmit((values) => onSubmit?.(values), onInvalid)} {...coreProps}>
                {children}
            </FormBridge>
        </FormProvider>
    );
}

type FormBridgeProps = Omit<CoreFormProps, "errors" | "children"> & {
    children: ReactNode;
};

/**
 * Inner bridge — must live inside `FormProvider` so `useFormState` subscribes
 * to error changes (reading `formState.errors` off `useFormContext()` is the
 * RHF subscription footgun and won't re-render reliably).
 */
function FormBridge({ onSubmit, children, ...coreProps }: FormBridgeProps) {
    const { errors } = useFormState();
    return (
        <CoreForm {...coreProps} noValidate errors={flattenFieldErrors(errors)} onSubmit={onSubmit}>
            {children}
        </CoreForm>
    );
}

// ── SplitRow ───────────────────────────────────────────────────────────────

/**
 * A label-left, value-right row for something a form only *shows* — a
 * server-minted id, a health badge. Not a Field: there is no control, no name
 * and nothing to validate.
 *
 * The grid mirrors kore's `fieldVariants({ layout: "split" })` so these rows
 * line up with the editable ones around them. Copied rather than imported
 * because that CVA is internal to the Form primitive; one copy, here, next to
 * the Field that uses the real thing.
 */
export function SplitRow({ label, children }: { label: ReactNode; children: ReactNode }) {
    return (
        <div className="flex flex-col gap-2 md:grid md:grid-cols-[minmax(0,12rem)_1fr] md:items-start md:gap-x-4 md:gap-y-0">
            <span className="text-sm font-medium leading-normal text-kore-text-muted">{label}</span>
            <div className="min-w-0">{children}</div>
        </div>
    );
}

// ── Field connector ────────────────────────────────────────────────────────

export interface FieldProps<
    TFieldValues extends FieldValues = FieldValues,
    TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
> extends Pick<ControllerProps<TFieldValues, TName>, "name" | "control" | "rules" | "render"> {
    /** Field label. Omit to render no label (e.g. visually-hidden custom labelling). */
    label?: ReactNode;
    /** Help text rendered between label and control. */
    description?: ReactNode;
    /** Append a decorative required asterisk to the label. */
    required?: boolean;
    /** Append the muted "optional" tag to the label. */
    optional?: boolean;
    /** Field layout: `stacked` (default) or `split` two-column. */
    layout?: FormFieldProps["layout"];
}

/**
 * Connector pairing RHF's `Controller` with the kore `Form` parts. Lays out
 * label -> control -> description -> message and binds the control through
 * the render prop:
 *
 *   <Field name="email" label="Email" render={({ field }) => <Input {...field} />} />
 */
export function Field<
    TFieldValues extends FieldValues = FieldValues,
    TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
>({ name, control, rules, render, label, description, required, optional, layout }: FieldProps<TFieldValues, TName>) {
    const control_ = (
        <>
            <CoreForm.Control>
                <Controller name={name} control={control} rules={rules} render={render} />
            </CoreForm.Control>
            {description != null ? <CoreForm.Description>{description}</CoreForm.Description> : null}
            <CoreForm.Message />
        </>
    );
    return (
        <CoreForm.Field name={name} layout={layout}>
            {label != null ? (
                <CoreForm.Label required={required} optional={optional}>
                    {label}
                </CoreForm.Label>
            ) : null}
            {/* Split is a two-column grid, so the control, its help and its
                message have to arrive as ONE child — left loose they are placed
                in order and the help lands in the label's column. Stacked keeps
                them loose, where the field root's own flex column already gives
                them the right rhythm and a wrapper would only add a node. */}
            {layout === "split" ? <div className="flex flex-col gap-2">{control_}</div> : control_}
        </CoreForm.Field>
    );
}
